import { Clickable, Col, H2, P, PortableText, Row, Spinner, Strong, Table, TypeWrapper, Wrapper } from '@ahmdigital/ui';
import { head, map, noop } from 'lodash/fp';
import { useQuery } from '@apollo/client';
import React from 'react';

import { Rate } from '../../../../../types/__codegen__/sanity/graphql';
import Calculator from '../calculator';
import CalculatorResults from './components/calculator-results';
import constants from '../../../../../ahm-constants';
import enableTaxReminder from './utils/enable-tax-reminder';
import GET_TAX_RATES from '../../queries/get-tax-rates';
import getPrefixSentence from './utils/get-prefix-sentence';
import getScaleTableData from './utils/get-scale-table-data';
import getThresholdsFromTaxRate from './utils/get-thresholds-from-tax-rate';
import styles from './styles.module.sass';

type CalculatorSectionProps = {
  customer?: {};
  incomeType?: string;
  mlsAmount?: number;
  onChange: (...args: unknown[]) => unknown;
  onIncomeChange?: (...args: unknown[]) => unknown;
  onScaleChange?: (...args: unknown[]) => unknown;
  onStateChange?: (...args: unknown[]) => unknown;
};

const CalculatorSection = ({ mlsAmount, incomeType, ...calculatorProps }: CalculatorSectionProps) => {
  const currentYear = new Date().getFullYear();
  const showTaxReminder = enableTaxReminder();
  const { data, loading } = useQuery(GET_TAX_RATES);

  if (!data) return null;

  const taxRates = data?.allRate;

  const activeTaxRate = head(taxRates) as Rate;

  const thresholds = getThresholdsFromTaxRate(activeTaxRate, incomeType);

  return (
    <Wrapper className={styles.calculatorWrapper}>
      <Row>
        <Col df={8} dfOffset={2} sm={12} smOffset={0} variant="marginBottomSpacer1">
          <TypeWrapper variant="center">
            <H2 variant="marginBottomSpacer4">How much extra tax could you be paying?</H2>
            <P>
              The Medicare Levy Surcharge (MLS) is set by the Government and is designed to make health care more
              affordable to more Australians. But if you earn more than the relevant income threshold, and don’t have an
              appropriate level of private hospital cover, it could be costing you extra in tax.
            </P>
            <P>
              Depending on your income over a full financial year, you might have to pay the MLS which can be an extra
              1%, 1.25% or 1.5% of your income on top of your annual income tax.{' '}
              <Strong>See table below to find your income threshold.</Strong> Find out more at{' '}
              <Clickable to="https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy-surcharge/medicare-levy-surcharge-income-thresholds-and-rates">
                ato.gov.au.
              </Clickable>
            </P>
          </TypeWrapper>
          {loading && <Spinner />}
          {!loading &&
            map((rate) => {
              const scaleTable = getScaleTableData(rate);
              return (
                <div key={rate.title}>
                  <TypeWrapper variant={['marginTopSpacer4', 'marginBottomSpacer10', 'center']}>
                    <PortableText source={rate.summary} />
                  </TypeWrapper>
                  <TypeWrapper variant={['marginTopSpacer8', 'marginBottomSpacer10']}>
                    <Strong>{rate.title}</Strong>
                  </TypeWrapper>
                  <TypeWrapper variant="center">
                    <Table {...{ ...scaleTable }} />
                  </TypeWrapper>
                </div>
              );
            }, taxRates)}
          <TypeWrapper variant="center">
            <P className={styles.paragraphBreak}>
              If you have two or more MLS dependant children, the threshold will increase by $1,500 for each MLS
              dependant child after your first child. Find out more at{' '}
              <Clickable to="https://www.ato.gov.au/Individuals/Medicare-and-private-health-insurance/Medicare-levy-surcharge/Family-and-dependants-for-Medicare-levy-surcharge-purposes/">
                ato.gov.au.
              </Clickable>
            </P>
            <P>
              <Strong>Use the calculator below to see how the MLS could affect you.</Strong>
            </P>
            <P>
              Please note the below calculations apply{' '}
              {showTaxReminder
                ? ` up until 30 June ${currentYear}. From 1 July ${currentYear} onwards, the new thresholds listed above will apply`
                : `from 1 July ${currentYear}`}
              . If you’re unsure whether the MLS applies to you, you should speak to a financial advisor.
            </P>
          </TypeWrapper>
        </Col>
      </Row>
      <Row>
        <Col df={10} dfOffset={1} variant="marginBottomSpacer2">
          {!loading && activeTaxRate && <Calculator {...calculatorProps} activeTaxRate={activeTaxRate} />}
        </Col>
      </Row>
      <Row>
        <Col df={8} dfOffset={2} sm={12} smOffset={0} variant="marginBottomSpacer8">
          {/* @ts-expect-error - Automatic, Please fix when editing this file */}
          <CalculatorResults amount={mlsAmount} />
        </Col>
      </Row>
      <Row>
        <Col df={8} dfOffset={2} sm={12} smOffset={0} variant="marginBottomSpacer2">
          <TypeWrapper variant="center">
            {incomeType === constants.INCOME_TYPE.SINGLE ? (
              <P>
                This is a guide only{showTaxReminder && ` and the thresholds are changing on 1 July ${currentYear}`}. It
                is based
                {/* @ts-expect-error - Automatic, Please fix when editing this file */} on{' '}
                {getPrefixSentence(calculatorProps.customer, thresholds)} the income you have selected and assumes you
                have no dependants^. You should speak to a financial advisor who will be able to take into account your
                income and personal situation, including any changes that occur during a tax year.
              </P>
            ) : (
              <P>
                This is a guide only{showTaxReminder && ` and the thresholds are changing on 1 July ${currentYear}`}. It
                is based {/* @ts-expect-error - Automatic, Please fix when editing this file */}
                on {getPrefixSentence(calculatorProps.customer, thresholds)} the family income you have selected and
                assumes you have 0-1 MLS dependent children^. Family income thresholds are increased by $1,500 for each
                MLS dependent child after the first child. So, if you have two MLS dependent children, the income
                threshold of $186,000 increases to $187,500 before you have to pay the MLS. You should speak to a
                financial advisor who will be able to take into account your income and personal situation, including
                any changes that occur during a tax year.
              </P>
            )}
            <P>
              ^ ‘Income’ and ‘dependants’ have{' '}
              <Clickable to={constants.URLS.THIRD_PARTY.ATO_MEDICARE_LEVY_SURCHARGE}>
                special meanings for MLS purposes
              </Clickable>
            </P>
          </TypeWrapper>
        </Col>
      </Row>
    </Wrapper>
  );
};

CalculatorSection.defaultProps = {
  customer: undefined,
  incomeType: constants.INCOME_TYPE.SINGLE,
  mlsAmount: 0,
  onIncomeChange: noop,
  onScaleChange: noop,
  onStateChange: noop,
};

export default CalculatorSection;
